/**
 * API error codes (plan §5.6) → i18n keys (§13.9). `null` = unknown code: the
 * caller shows its screen's fallback copy. Read the code and status of an
 * axios error with `getServerErrorCode` / `getServerErrorStatus`
 * (`@/domain/reservations/reservation-errors`).
 */

/** Which call a `PLACES_UNAVAILABLE` came from: the search box or the address geocode. */
export type TPlacesErrorContext = 'search' | 'geocode';

const SETUP_ERROR_KEYS: ReadonlyMap<string, string> = new Map([
  ['SLUG_TAKEN', 'storeSetup.business.linkTaken'],
  ['SLUG_INVALID', 'storeSetup.business.linkRules'],
  ['NAME_INVALID', 'storeSetup.business.nameError'],
  ['HOURS_INVALID', 'storeSetup.hours.invalid'],
  ['CURRENCY_INVALID', 'storeSetup.business.currencyRequired'],
  ['LOCATION_UNRESOLVED', 'storeSetup.location.geocodeNotFound'],
  ['ORDERS_INVALID', 'storeSetup.orders.saveError'],
  ['SETUP_NOT_FOUND', 'storeSetup.errors.generic'],
]);

const MENU_IMPORT_ERROR_KEYS: ReadonlyMap<string, string> = new Map([
  ['UNSUPPORTED_FORMAT', 'menuImport.errors.unsupported'],
  ['TOO_SMALL', 'menuImport.errors.tooSmall'],
  ['PAGE_LIMIT', 'menuImport.errors.pageLimit'],
  ['TEXT_TOO_LONG', 'menuImport.paste.tooLong'],
  ['READ_RUNNING', 'menuImport.errors.busy'],
  ['NO_PAGES', 'menuImport.errors.busy'],
  ['TURN_IN_PROGRESS', 'menuImport.errors.turnInProgress'],
  ['PROPOSAL_STALE', 'menuImport.proposal.stale'],
  ['NOTHING_TO_UNDO', 'menuImport.errors.nothingToUndo'],
  ['DRAFT_CHANGED', 'menuImport.publish.changed'],
  ['NOTHING_VISIBLE', 'menuImport.publish.nothingVisible'],
  ['QUOTA_READS', 'menuImport.quota.reads'],
  ['SESSION_BUDGET', 'menuImport.reasons.SESSION_BUDGET'],
  ['QUOTA_MESSAGES', 'menuImport.quota.messages'],
  ['AI_UNAVAILABLE', 'menuImport.reasons.AI_UNAVAILABLE'],
  ['COPY_EMPTY', 'menuImport.errors.copyEmpty'],
  ['COPY_TOO_LARGE', 'menuImport.errors.copyTooLarge'],
  ['SESSION_NOT_EMPTY', 'menuImport.errors.sessionNotEmpty'],
  ['ITEM_LIMIT', 'menuImport.errors.itemLimit'],
  // The draft would outgrow the server's size cap (long texts, many add-on groups).
  ['DRAFT_TOO_LARGE', 'menuImport.errors.draftTooLarge'],
  ['MULTIPLE_MENUS', 'menuImport.errors.generic'],
  // Another device or tab published the session (an upload's tile; the flow moves on itself).
  ['SESSION_PUBLISHED', 'menuImport.publishedToast'],
]);

/** Setup-screen codes; `SLUG_TAKEN` names the text of the suggestions UI, `ORDERS_ALREADY_SET` is a success (see `isOrdersAlreadySet`). */
export function setupErrorKey(
  code: string | null | undefined,
  context: TPlacesErrorContext = 'search'
): string | null {
  if (code === 'PLACES_UNAVAILABLE')
    return context === 'geocode'
      ? 'storeSetup.location.geocodeFailed'
      : 'storeSetup.location.searchFailed';
  return SETUP_ERROR_KEYS.get(code ?? '') ?? null;
}

/** A second «Pedidos» save answers 409 `ORDERS_ALREADY_SET`: the admin treats it as success. */
export function isOrdersAlreadySet(code: string | null | undefined): boolean {
  return code === 'ORDERS_ALREADY_SET';
}

/** Menu-import codes; an HTTP 413 (multer's size limit, no code) → «La foto es muy pesada». */
export function menuImportErrorKey(
  code: string | null | undefined,
  status: number | null | undefined
): string | null {
  if (status === 413) return 'menuImport.errors.fileTooLarge';
  return MENU_IMPORT_ERROR_KEYS.get(code ?? '') ?? null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `details.suggestions` of a 409 `SLUG_TAKEN` (free slugs the server checked); [] otherwise. */
export function slugTakenSuggestions(error: unknown): string[] {
  if (!isRecord(error) || !isRecord(error.response)) return [];
  const data = error.response.data;
  if (!isRecord(data) || data.code !== 'SLUG_TAKEN' || !isRecord(data.details))
    return [];
  const suggestions = data.details.suggestions;
  return Array.isArray(suggestions)
    ? suggestions.filter(
        (suggestion): suggestion is string => typeof suggestion === 'string'
      )
    : [];
}
